import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Heading from '../chat/Heading';
import { Container } from '../../styles/chime-react/CommonStyles';
import PrimaryButton from '../chat/Button/PrimaryButton';
import { useIdentityService } from '../../providers/IdentityProvider';
import { Step, StepIconProps, Stepper } from '@mui/material';
import {
  CustomStepConnector,
  CustomStepIconRoot,
  CustomStepLabel,
  StyledCloseLink,
  StyledCloseLinkWrap,
  StyledContentBox,
  StyledExplanationBox,
  StyledStepButtonWrap,
  StyledStepExplanation
} from './Styled';
import WalkthroughEnd from './WalkthroughEnd';
import Image from 'next/image';
import WalkthroughCompany from './WalkthroughCompany';
import WalkthroughInquiry from './WalkthroughInquiry';
import Flag from '../icons/Flag';
import userExplainImage from '../../public/img_user_explain.svg';
import userHandsUpImage from '../../public/img_user_hands_up.svg';
import { useLoading } from '../../providers/LoadingProvider';
import { AccountStatus } from '../../constants';

export const CustomStepIcon = (props: StepIconProps) => {
  const { active, completed, className } = props;

  return (
    <CustomStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      <Flag className="CustomStepIcon-flagIcon" />
      <div className="CustomStepIcon-circle" />
    </CustomStepIconRoot>
  );
};

const Walkthrough: FC<{
  onClose: () => void;
  onEndStep?: (flag: boolean) => void;
}> = ({ onClose, onEndStep }) => {
  const [currentStep, setCurrentStep] = useState<'company' | 'inquiry' | 'end'>(
    'inquiry'
  );
  const { useCompany, identityClient } = useIdentityService();
  const data = useCompany(true);
  const [flag, setFlag] = useState<boolean>(true);
  const [avatar, setAvatar] = useState<Blob>();
  const { setLoading } = useLoading();

  const getComponent = useCallback(
    (step: typeof currentStep) => {
      switch (step) {
        case 'inquiry':
          return <WalkthroughInquiry value={flag} onChange={setFlag} />;
        case 'company':
          return (
            <WalkthroughCompany setAvatar={setAvatar} company={data.data} />
          );
        case 'end':
          return <WalkthroughEnd onClose={onClose} />;
      }
    },
    [data, flag, currentStep]
  );

  const getCallback = useCallback(
    (step: typeof currentStep) => {
      switch (step) {
        case 'inquiry':
          return () => setCurrentStep('company');
        case 'company':
          return () => {
            setLoading(true);
            identityClient.setCompanyState(AccountStatus.InUse).then(() =>
              Promise.all(
                [
                  identityClient.updateDisplayLineInquiryButton({
                    displayLineInquiryButtonFLG: flag
                  }),
                  avatar &&
                    identityClient.updateCompanyProfile({
                      fileData: new File([avatar], '')
                    })
                ].filter(Boolean)
              )
                .then(() => {
                  data.mutate();
                  setCurrentStep('end');
                  onEndStep?.(flag);
                })
                .finally(() => setLoading(false))
            );
          };
      }
    },
    [data, flag]
  );

  const steps = [
    {
      key: 'inquiry',
      value: '１',
      explanation: '「LINE問合せ」「メッセージ問合せ」ボタンの設定を始めます',
      image: userExplainImage,
      button: '次へ'
    },
    {
      key: 'company',
      value: '２',
      explanation: 'エンドユーザーに表示する会社名とアイコンを設定します',
      image: userExplainImage,
      button: '初期設定を完了する'
    },
    {
      key: 'end',
      value: '申込完了',
      explanation:
        '初期設定が完了しました。\n１時間ほどでスマートフォンサイトに「LINE問合せ」ボタン、アットホームアプリに「メッセージ問合せ」ボタンの表示が始まります。\n※PCサイトには表示されません。',
      image: userHandsUpImage
    }
  ];
  const stepData = useMemo(
    () => steps.find((step) => step.key === currentStep),
    [currentStep]
  );

  const stepIndex = useMemo(() => steps.indexOf(stepData!), [stepData]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [stepIndex]);

  return (
    <Container>
      <Heading
        level={1}
        tag="h1"
        alignCenter={true}
        color="primary"
        mb="2.5rem"
      >
        初期設定
      </Heading>
      <StyledContentBox>
        <Stepper
          alternativeLabel
          activeStep={stepIndex}
          connector={<CustomStepConnector />}
          sx={{
            width: 510,
            margin: 'auto'
          }}
        >
          {steps.map((step) => (
            <Step key={step.key}>
              <CustomStepLabel
                $smallText={step.key === 'end'}
                StepIconComponent={CustomStepIcon}
              >
                {step.value}
              </CustomStepLabel>
            </Step>
          ))}
        </Stepper>
        <StyledStepExplanation>
          <StyledExplanationBox>{stepData?.explanation}</StyledExplanationBox>
          {!!stepData?.image && (
            <Image
              className="explanation-image"
              alt={stepData?.value}
              src={stepData.image}
            />
          )}
        </StyledStepExplanation>
        {getComponent(currentStep)}
        {!!stepData?.button && (
          <>
            <StyledStepButtonWrap>
              <PrimaryButton
                label={stepData.button}
                onClick={() => getCallback(currentStep)?.()}
              />
            </StyledStepButtonWrap>
            <StyledCloseLinkWrap>
              <StyledCloseLink
                onClick={() => {
                  window.opener = null;
                  window.open('', '_self');
                  window.close();
                }}
              >
                中止してこの画面を閉じる
              </StyledCloseLink>
            </StyledCloseLinkWrap>
          </>
        )}
      </StyledContentBox>
    </Container>
  );
};

export default Walkthrough;
